"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, BookOpen, Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface ArticleCopy {
  sectionEyebrow: string;
  sectionTitle: string;
  sectionLead: string;
  viewAllBtn: string;
  loadingError: string;
  retryBtn: string;
  emptyTitle: string;
  emptyDesc: string;
  defaultCategory: string;
  defaultCoverAlt: string;
  defaultExcerpt: string;
  readBtn: string;
  pageOf: (current: number, total: number) => string;
  prevBtn: string;
  nextBtn: string;
  locale: string;
}
const copyData: Record<Lang, ArticleCopy> = {
  en: {
    sectionEyebrow: "Technical Resources & Articles",
    sectionTitle: "Technical articles on polymer melt filtration",
    sectionLead: "Review filtration dynamics, pressure drop calculations, and alloy selection for high-temperature extrusion screens.",
    viewAllBtn: "View all technical articles",
    loadingError: "Unable to load polymer filtration articles.",
    retryBtn: "Retry loading",
    emptyTitle: "No technical articles found",
    emptyDesc: "Articles on polymer melt wire mesh and filtration mechanisms will appear here once published.",
    defaultCategory: "Technical Guide",
    defaultCoverAlt: "Technical article cover",
    defaultExcerpt: "Technical evaluation of stainless steel woven wire mesh specifications and operating parameters in polymer melt processes.",
    readBtn: "Read technical article",
    pageOf: (c, t) => `Page ${c} of ${t}`,
    prevBtn: "Previous",
    nextBtn: "Next",
    locale: "en-US"
  },
  zh: {
    sectionEyebrow: "技术资源与文章",
    sectionTitle: "聚合物熔体过滤技术文章",
    sectionLead: "查阅关于高温挤出过滤网的过滤动力学、压降计算及合金选型的技术文献。",
    viewAllBtn: "查看全部技术文章",
    loadingError: "无法加载聚合物过滤技术文章。",
    retryBtn: "重新加载",
    emptyTitle: "暂无技术文章",
    emptyDesc: "有关聚合物熔体丝网与过滤机理的文章发布后将在此显示。",
    defaultCategory: "技术指南",
    defaultCoverAlt: "技术文章配图",
    defaultExcerpt: "聚合物熔体加工中不锈钢编织丝网规格与运行参数的技术评估与分析。",
    readBtn: "阅读技术文章",
    pageOf: (c, t) => `第 ${c} 页 / 共 ${t} 页`,
    prevBtn: "上一页",
    nextBtn: "下一页",
    locale: "zh-CN"
  },
  es: {
    sectionEyebrow: "Recursos técnicos y artículos",
    sectionTitle: "Artículos técnicos sobre filtración de polímeros fundidos",
    sectionLead: "Consulte dinámica de filtración, cálculo de caída de presión y selección de aleaciones para filtros de extrusión a alta temperatura.",
    viewAllBtn: "Ver todos los artículos técnicos",
    loadingError: "No se pudieron cargar los artículos de filtración de polímeros.",
    retryBtn: "Reintentar carga",
    emptyTitle: "No se encontraron artículos técnicos",
    emptyDesc: "Los artículos sobre mallas de alambre y mecanismos de filtración en polímeros fundidos aparecerán aquí tras su publicación.",
    defaultCategory: "Guía técnica",
    defaultCoverAlt: "Imagen del artículo técnico",
    defaultExcerpt: "Evaluación técnica de especificaciones de malla de acero inoxidable y parámetros operativos en procesos de polímeros fundidos.",
    readBtn: "Leer artículo técnico",
    pageOf: (c, t) => `Página ${c} de ${t}`,
    prevBtn: "Anterior",
    nextBtn: "Siguiente",
    locale: "es-ES"
  },
  de: {
    sectionEyebrow: "Technische Ressourcen & Fachartikel",
    sectionTitle: "Fachartikel zur Polymerschmelzefiltration",
    sectionLead: "Filtrationsdynamik, Differenzdruckberechnungen und Werkstoffauswahl für Hochtemperatur-Extrusionssiebe im Überblick.",
    viewAllBtn: "Alle Fachartikel anzeigen",
    loadingError: "Fachartikel zur Polymerfiltration konnten nicht geladen werden.",
    retryBtn: "Erneut laden",
    emptyTitle: "Keine Fachartikel gefunden",
    emptyDesc: "Artikel zu Drahtgeweben und Filtrationsmechanismen bei Polymerschmelzen werden hier nach Veröffentlichung angezeigt.",
    defaultCategory: "Technischer Leitfaden",
    defaultCoverAlt: "Titelbild Fachartikel",
    defaultExcerpt: "Technische Bewertung von Edelstahldrahtgewebe-Spezifikationen und Betriebsparametern in der Polymerschmelzeverarbeitung.",
    readBtn: "Fachartikel lesen",
    pageOf: (c, t) => `Seite ${c} von ${t}`,
    prevBtn: "Zurück",
    nextBtn: "Weiter",
    locale: "de-DE"
  },
  fr: {
    sectionEyebrow: "Ressources techniques & articles",
    sectionTitle: "Articles techniques sur la filtration des polymères en fusion",
    sectionLead: "Consultez la dynamique de filtration, les calculs de perte de charge et le choix des alliages pour toiles d'extrusion haute température.",
    viewAllBtn: "Voir tous les articles techniques",
    loadingError: "Impossible de charger les articles sur la filtration des polymères.",
    retryBtn: "Réessayer",
    emptyTitle: "Aucun article technique trouvé",
    emptyDesc: "Les articles consacrés aux toiles métalliques et aux mécanismes de filtration des polymères apparaîtront ici dès publication.",
    defaultCategory: "Guide technique",
    defaultCoverAlt: "Illustration article technique",
    defaultExcerpt: "Évaluation technique des toiles tissées en acier inoxydable et des paramètres opérationnels dans les procédés de fusion polymère.",
    readBtn: "Lire l'article technique",
    pageOf: (c, t) => `Page ${c} sur ${t}`,
    prevBtn: "Précédent",
    nextBtn: "Suivant",
    locale: "fr-FR"
  },
  pt: {
    sectionEyebrow: "Recursos técnicos e artigos",
    sectionTitle: "Artigos técnicos sobre filtração de polímeros fundidos",
    sectionLead: "Consulte dinâmica de filtração, cálculos de perda de carga e seleção de ligas para telas de extrusão a alta temperatura.",
    viewAllBtn: "Ver todos os artigos técnicos",
    loadingError: "Não foi possível carregar os artigos sobre filtração de polímeros.",
    retryBtn: "Tentar novamente",
    emptyTitle: "Nenhum artigo técnico encontrado",
    emptyDesc: "Artigos sobre telas de arame e mecanismos de filtração em polímeros fundidos serão exibidos aqui após a publicação.",
    defaultCategory: "Guia técnico",
    defaultCoverAlt: "Capa do artigo técnico",
    defaultExcerpt: "Avaliação técnica das especificações de malha em aço inoxidável e parâmetros operacionais em processos de polímeros fundidos.",
    readBtn: "Ler artigo técnico",
    pageOf: (c, t) => `Página ${c} de ${t}`,
    prevBtn: "Anterior",
    nextBtn: "Próximo",
    locale: "pt-PT"
  }
};
function ArticlesGridConsumer({
  copy
}: {
  copy: ArticleCopy;
}) {
  const {
    posts,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({
        length: 6
      }).map((_, i) => <Card key={i} data-index={i} className="flex flex-col border border-border bg-card overflow-hidden">
            <div className="mf-card-media mf-ratio-card w-full bg-muted" />
            <CardHeader className="space-y-3">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-16 w-full" />
            </CardContent>
            <CardFooter className="mt-auto border-t border-border pt-4">
              <Skeleton className="h-8 w-28" />
            </CardFooter>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-8 text-center">
        <p className="text-foreground font-medium mb-3">
          {copy.loadingError}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          {copy.retryBtn}
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="rounded-lg border border-border bg-card p-10 text-center">
        <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary mx-auto mb-3">
          <BookOpen className="size-5" aria-hidden="true" />
        </div>
        <h3 className="mf-h3 text-foreground">
          {copy.emptyTitle}
        </h3>
        <p className="text-muted-foreground text-sm mt-1 max-w-md mx-auto">
          {copy.emptyDesc}
        </p>
      </div>;
  }
  return <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {posts.map((post, i) => {
        const categoryName = post.categories?.[0]?.name || copy.defaultCategory;
        const formattedDate = post.date ? new Date(post.date).toLocaleDateString(copy.locale, {
          year: "numeric",
          month: "short",
          day: "numeric"
        }) : null;
        return <Card key={post.id} data-index={i} className="flex flex-col border border-border bg-card hover:border-primary/40 transition-colors duration-200 overflow-hidden">
              {post.featuredImage ? <div className="relative mf-card-media mf-ratio-card w-full">
                  <img src={post.featuredImage} alt={post.title || copy.defaultCoverAlt} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                </div> : <div className="mf-card-media mf-ratio-card w-full bg-muted/60 flex items-center justify-center text-muted-foreground">
                  <BookOpen className="size-5" aria-hidden="true" />
                </div>}

              <CardHeader className="space-y-2.5">
                <div className="flex items-center justify-between gap-2">
                  <Badge variant="outline" className="mf-caption font-medium border-border bg-card">
                    {categoryName}
                  </Badge>
                  {formattedDate && <span className="flex items-center gap-1 mf-caption text-muted-foreground font-mono">
                      <SafeHydrate><Calendar className="size-3.5" aria-hidden="true" /></SafeHydrate>
                      {formattedDate}
                    </span>}
                </div>
                <CardTitle className="mf-h3 leading-snug text-foreground line-clamp-2">
                  {post.title}
                </CardTitle>
              </CardHeader>

              <CardContent className="flex-1">
                {post.excerpt ? <p className="text-sm text-muted-foreground line-clamp-3 leading-relaxed">
                    {post.excerpt.replace(/<[^>]+>/g, "")}
                  </p> : <p className="text-sm text-muted-foreground line-clamp-3 leading-relaxed">
                    {copy.defaultExcerpt}
                  </p>}
              </CardContent>

              <CardFooter className="border-t border-border pt-4">
                <Button asChild variant="ghost" size="sm" className="w-full justify-between px-2 text-primary hover:text-primary">
                  <Link to={post.link || "/TechnicalResourcesPage"}>
                    <span>{copy.readBtn}</span>
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>;
      })}
      </div>

      {(hasNext || hasPrev) && <div className="flex items-center justify-between border-t border-border pt-6">
          <div className="mf-caption font-mono text-muted-foreground">
            {copy.pageOf(currentPage, totalPages || 1)}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => prevPage()} disabled={!hasPrev} className="gap-1">
              <ChevronLeft className="size-4" aria-hidden="true" />
              <span>{copy.prevBtn}</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => nextPage()} disabled={!hasNext} className="gap-1">
              <span>{copy.nextBtn}</span>
              <ChevronRight className="size-4" aria-hidden="true" />
            </Button>
          </div>
        </div>}
    </div>;
}
export default function RelatedArticles() {
  const {
    lang
  } = useLanguage();
  const copy = pick(lang, copyData);
  return <section id="relatedpolymermeltarticles" data-nav="light" className="bg-background mf-section border-b border-border">
      <div className="mf-container">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10 pb-6 border-b border-border">
          <div className="max-w-2xl space-y-2">
            <div className="inline-flex items-center gap-2 mf-caption font-mono uppercase tracking-wider text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              {copy.sectionEyebrow}
            </div>
            <h2 className="mf-h2 font-semibold text-foreground">
              {copy.sectionTitle}
            </h2>
            <p className="mf-lead text-muted-foreground">
              {copy.sectionLead}
            </p>
          </div>
          <div className="shrink-0">
            <Button asChild variant="outline">
              <Link to="/TechnicalResourcesPage" className="inline-flex items-center gap-2">
                <span>{copy.viewAllBtn}</span>
                <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <ArticlesGridConsumer copy={copy} />
        </PostsProvider>
      </div>
    </section>;
}